import React, {
  useCallback,
  useEffect,
} from 'react';

import { useSelector } from 'react-redux';

import { Button } from '@waglabs/button-next';
import {
  ChevronLeftIcon,
} from '@waglabs/icons';
import { Typography } from '@waglabs/typography';
import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';

import { LoginByPasswordFormStep } from './LoginByPasswordFormStep';
import { LoginByCodeFormStep } from './LoginByCodeFormStep';

import type { UseStepperReturnType } from '../../../../../../hooks/useStepper';
import {
  usePostAuthenticationCode,
} from '../../../../../../hooks/api/usePostAuthenticationCode';

import {
  getPreferredMethod,
  getAllowedMethodsExcludingPreferredMethod,
} from '../../../../../../reducers/auth/auth.selector';
import { Auth, DATA_TEST_IDS } from '../../../../../../constants';

type LoginByEmailFormStep2Props = UseStepperReturnType;

const buttonActionMapping = {
  [Auth.AllowedMethods.CodeEmail]: {
    stepName: LoginByCodeFormStep.name,
    text: 'Email me a sign in code',
    dataTestId: DATA_TEST_IDS.LOGIN.loginByEmailForm.EmailSignInWithCodeBtn,
  },
  [Auth.AllowedMethods.CodeSMS]: {
    stepName: LoginByCodeFormStep.name,
    text: 'Text me a sign in code',
    dataTestId: DATA_TEST_IDS.LOGIN.loginByEmailForm.EmailSignInWithCodeSmsBtn,
  },
  [Auth.AllowedMethods.Password]: {
    stepName: LoginByPasswordFormStep.name,
    text: 'Sign in via password',
    dataTestId: DATA_TEST_IDS.LOGIN.loginByEmailForm.EmailSignInWithPasswordBtn,
  },
};

export const LoginByEmailFormStep2 = ({
  handleBack,
  handleNext,
  setMeta,
}: LoginByEmailFormStep2Props) => {
  const {
    isLoading,
    data: authenticationCode,
    isSuccess: isSendAuthenticationCodeSuccess,
    execute: sendAuthenticationCode,
    reset,
  } = usePostAuthenticationCode();
  const primaryMethod = useSelector(getPreferredMethod);
  const primaryCTA = primaryMethod ? buttonActionMapping[primaryMethod] : buttonActionMapping[Auth.AllowedMethods.CodeEmail];
  const secondaryMethods = useSelector(
    getAllowedMethodsExcludingPreferredMethod,
  );
  /**
   * This ref is use to skip rendering the selection screen
   * if there's only a single authentication method to show
   */
  const hasPassThroughRef = React.useRef(false);

  const hasUnsupportedMethod = !primaryCTA || !primaryCTA.text;

  const isCodeFormStep = (stepName: string) => LoginByCodeFormStep.name === stepName;

  const handleSendingAuthenticationCode = useCallback(({ stepName }) => {
    setMeta({
      stepName,
      authenticationMethod: Auth.AllowedMethods.CodeEmail,
    });
    sendAuthenticationCode({
      authenticationMethod: Auth.AllowedMethods.CodeEmail,
    });
  }, [
    sendAuthenticationCode,
    setMeta,
  ]);

  const handleOnPrimaryClick = useCallback(() => {
    const shouldSendAuthenticationCode = isCodeFormStep(primaryCTA.stepName);
    if (!shouldSendAuthenticationCode) {
      setMeta({
        stepName: primaryCTA.stepName,
      });
      handleNext();
      return;
    }
    handleSendingAuthenticationCode({
      stepName: primaryCTA.stepName,
    });
  }, [
    handleNext,
    handleSendingAuthenticationCode,
    primaryCTA.stepName,
    setMeta,
  ]);

  // Second function argument is where the "event" object will land
  // (buttonMapping) => (event: SyntheticEvent<any>) => {}
  const handleOnSecondaryClick = (buttonMapping: { stepName: string }) => () => {
    const shouldSendAuthenticationCode = isCodeFormStep(buttonMapping.stepName);
    if (!shouldSendAuthenticationCode) {
      setMeta({
        stepName: buttonMapping.stepName,
      });
      handleNext();
      return;
    }
    handleSendingAuthenticationCode({
      stepName: buttonMapping.stepName,
    });
  };

  useEffect(() => {
    if (!isSendAuthenticationCodeSuccess) {
      return;
    }

    if (authenticationCode.preferredMethod) {
      setMeta((prevMeta: Record<string, unknown>) => ({
        ...prevMeta,
        authenticationMethod: authenticationCode.preferredMethod,
      }));
    }
    // reset it here knowing that it is a successful event; Allows the user to go back
    // Otherwise, the user will be stuck on the next page and unable to go back
    // because this effect will keep on running
    reset();
    handleNext();
  }, [
    reset,
    isSendAuthenticationCodeSuccess,
    handleNext,
    setMeta,
    authenticationCode.preferredMethod,
  ]);

  /**
   * Pass through the selection screen if there's only
   * a single authentication method to show
   */
  useEffect(() => {
    if (hasPassThroughRef.current) {
      return;
    }

    if (secondaryMethods.length) {
      return;
    }

    handleOnPrimaryClick();
    setMeta((prevMeta: Record<string, unknown>) => ({
      ...prevMeta,
      /**
       * Do not return here when calling `handleBack` from stepper
       * instead go to step 1
       * 
       * step 1 is where the user enters the email address
       * 
       * TODO - Make a way to easily determine each step number
       * or maybe use step name when going back?
       */
      previousBackStep: 1,
    }));

    hasPassThroughRef.current = true;
  }, [
    isSendAuthenticationCodeSuccess,
    secondaryMethods,
    handleOnPrimaryClick,
    setMeta,
  ]);

  /**
   * To prevent from noticing the "flashing of component",
   * we don't render anything if we know we're going
   * to let it pass through.
   * 
   * See `hasPassThroughRef` useEffect usage
   */
  if (!secondaryMethods.length) {
    return null;
  }

  if (hasUnsupportedMethod) {
    return null;
  }

  return (
    <>
      <Flex
        height="24px"
        alignItems="center"
        onClick={() => {
          handleBack();
        }}
        hover
      >
        <ChevronLeftIcon
          color="wagGreen"
          cursor="pointer"
        />
        <Typography
          m={0}
          color="wagGreen"
          hover
        >
          Back
        </Typography>
      </Flex>
      <Box
        display="grid"
        gridTemplateColumns={['1fr']}
        gridGap="8px"
        px={['unset', '72px']}
        py="24px"
      >
        <Button
          variant="contained"
          onClick={handleOnPrimaryClick}
          disabled={isLoading}
          data-testId={primaryCTA.dataTestId}
        >
          {primaryCTA.text}
        </Button>
        {/* secondary cta */}
        {secondaryMethods.map(secondaryMethod => (
          <Button
            key={buttonActionMapping[secondaryMethod].text}
            variant="text"
            onClick={handleOnSecondaryClick(buttonActionMapping[secondaryMethod])}
            disabled={isLoading}
            data-testId={buttonActionMapping[secondaryMethod].dataTestId}
          >
            {buttonActionMapping[secondaryMethod].text}
          </Button>
        ))}
      </Box>
    </>
  );
};
